import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Row,
  Col,
} from 'antd';
import { HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Text } = Typography;

const DepartmentForm = ({ open, onClose, onSubmit, department = null, loading = false }) => {
  const [form] = Form.useForm();
  const [color, setColor] = useState('#3498db');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      if (department) {
        form.setFieldsValue({
          name: department.name || '',
          description: department.description || '',
        });
        setColor(department.colorHex || '#3498db');
      } else {
        form.resetFields();
        setColor('#3498db');
      }
      setShowColorPicker(false);
    }
  }, [department, open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit({
        ...values,
        colorHex: color,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const ColorPreview = () => (
    <div
      style={{
        width: 44,
        height: 44,
        backgroundColor: color,
        border: '2px solid #d9d9d9',
        borderRadius: 8,
        cursor: 'pointer',
      }}
      onClick={() => setShowColorPicker(!showColorPicker)}
    />
  );

  return (
    <Modal
      title={department ? t('department.editDepartment') : t('department.addNew')}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          {department ? t('common.update') : t('common.create')}
        </Button>,
      ]}
      width="min(600px, 95vw)"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="name"
          label={t('department.form.nameLabel')}
          rules={[
            {
              required: true,
              message: t('department.form.nameRequired'),
            },
            {
              max: 100,
              message: t('department.form.nameMaxLength'),
            },
          ]}
        >
          <Input placeholder={t('department.form.namePlaceholder')} disabled={loading} />
        </Form.Item>

        <Form.Item label={t('department.form.colorLabel')}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={16} align="middle">
              <Col>
                <ColorPreview />
              </Col>
              <Col>
                <Input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#3498db"
                  style={{ width: 120 }}
                  disabled={loading}
                />
              </Col>
              <Col>
                <Text type="secondary">{t('department.form.colorHint')}</Text>
              </Col>
            </Row>

            {showColorPicker && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                <HexColorPicker
                  color={color}
                  onChange={setColor}
                />
              </div>
            )}
          </Space>
        </Form.Item>

        <Form.Item
          name="description"
          label={t('department.form.descriptionLabel')}
          rules={[
            {
              max: 500,
              message: t('department.form.descriptionMaxLength'),
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder={t('department.form.descriptionPlaceholder')}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DepartmentForm;
